import React, { forwardRef } from "react";
import { BaseElementProps, ThemeColor, PanelColor } from "../../props";
import {
  getBackgroundStyles,
  getForegroundStyles,
  getBorderStyles,
  cn,
} from "../../styles";

export interface PanelProps extends BaseElementProps {
  /**
   * Content to display inside the panel.
   */
  children?: React.ReactNode;
  /**
   * Color theme for the panel.
   * Can be a semantic theme color or hierarchical panel level color.
   * Panel levels (level0-level11) provide graduated backgrounds for visual hierarchy.
   * @default PanelColor.level1
   */
  color?: ThemeColor | PanelColor;
  /**
   * Applies rounded corners to the panel.
   * @default true
   */
  rounded?: boolean;
  /**
   * Applies shadow effect to the panel for depth.
   * @default true
   */
  shadow?: boolean;
  /**
   * Displays a border around the panel.
   * Border color automatically adjusts based on the panel color.
   * @default true
   */
  border?: boolean;
  /**
   * Reduces internal padding for a more compact appearance.
   * @default false
   */
  dense?: boolean;
}

/**
 * Versatile container component for grouping and styling content.
 * Provides consistent styling with customizable appearance through color, shadow, border, and rounding.
 * Supports both semantic theme colors and hierarchical panel levels for visual depth.
 * Ideal for cards, sections, and any content that needs visual separation or elevation.
 */
export const Panel = forwardRef<HTMLDivElement, PanelProps>(
  (
    {
      className,
      children,
      color = PanelColor.level1,
      rounded = true,
      shadow = true,
      border = true,
      dense = false,
      ...props
    },
    ref
  ) => {
    return (
      <div
        ref={ref}
        className={cn(
          getBackgroundStyles(color),
          getForegroundStyles(color),
          rounded && "rounded-md",
          shadow && "shadow-md",
          border && `border ${getBorderStyles(color)}`,
          dense ? "p-1" : "p-2",
          className
        )}
        {...props}
      >
        {children}
      </div>
    );
  }
);

Panel.displayName = "Panel";
